import z from "zod";

import {
    api_pkg_attendance_dto_FormattedAttendanceFeedbackSchema,
    api_pkg_attendance_dto_StatusSchema,
    api_pkg_attendance_dto_TypeSchema,
    Services_attendance_pkg_attendance_BreakSchema,
} from "@sdk/schemas";
import { createCall, spellbook } from "@sdk/spellbook";

import { withAccountOrNetworkAuthHeaders } from "./apiHelpers";

const endpoint = spellbook("/v2/attendances").GET();

const querySchema = endpoint.q.extend({
    // overwrite required fields
    startDateBefore: endpoint.q.shape.startDateBefore.unwrap().unwrap(),
    startDateAfter: endpoint.q.shape.startDateAfter.unwrap().unwrap(),
});

const responseSchema = endpoint.res.extend({
    // origin is typed as z.enum(["pixid"]) but the API returns other values for non-Pixid tasks
    tasks: z
        .array(endpoint.res.shape.tasks.unwrap().element.extend({ origin: z.string() }))
        .nullable(),
});

export type GetAttendancesV2Query = z.infer<typeof querySchema>;
type GetAttendancesV2RawResponse = z.infer<typeof responseSchema>;
type GetAttendancesV2RawAttendance = NonNullable<
    GetAttendancesV2RawResponse["attendances"]
>[number];

export type AttendanceTypeV2 = z.infer<typeof api_pkg_attendance_dto_TypeSchema>;
export type AttendanceStatusV2 = z.infer<typeof api_pkg_attendance_dto_StatusSchema>;
export type AttendanceBreakV2 = z.infer<typeof Services_attendance_pkg_attendance_BreakSchema>;
export type GetAttendancesV2Feedback = z.infer<
    typeof api_pkg_attendance_dto_FormattedAttendanceFeedbackSchema
>;
export type GetAttendancesV2Attendance = Omit<GetAttendancesV2RawAttendance, "status"> & {
    status: AttendanceStatusV2;
};
export type GetAttendancesV2Response = Omit<GetAttendancesV2RawResponse, "attendances"> & {
    attendances: GetAttendancesV2Attendance[] | null;
};
export type GetAttendancesV2Shift = NonNullable<GetAttendancesV2RawResponse["shifts"]>[number];

export async function getAttendancesV2({
    query,
    signal,
}: {
    query: GetAttendancesV2Query;
    signal?: AbortSignal;
}): Promise<GetAttendancesV2Response> {
    const call = createCall({ ...endpoint, res: responseSchema }, (r) =>
        fetch(withAccountOrNetworkAuthHeaders(r)),
    );
    const res = await call({ query, signal });

    return res;
}
